/**
 * @fileoverview Hook for profit over time chart data from dashboard data.
 * @module shared/hooks/dashboard/useProfitOverTime
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'
import { useFilter } from '@/shared/hooks/useFilter'

/**
 * Profit over time data point.
 */
export interface ProfitTimePoint {
  /** Period name */
  period: string
  /** Profit value */
  profit: number
  /** Revenue value */
  revenue: number
  /** Cost value */
  cost: number
}

/**
 * Hook result for profit over time.
 */
export interface UseProfitOverTimeResult {
  /** Time series data */
  data: ProfitTimePoint[]
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error message */
  error: string | null
}

/**
 * Hook to get profit over time chart data from dashboard data.
 *
 * @returns Profit over time data with loading and error states
 * @example
 * ```typescript
 * const { data, isLoading } = useProfitOverTime();
 * ```
 */
export function useProfitOverTime(): UseProfitOverTimeResult {
  const { salesTimeQuery } = useDashboardData()
  const { profitBasis } = useFilter()

  const data = useMemo(() => {
    const dataPoints = salesTimeQuery.data?.data || []

    // Helper to parse various period formats (YYYY-MM-DD, YYYY-MM, "Jan 2024")
    const toTimestamp = (period: string | undefined): number => {
      const p = period?.trim() || ''
      const m1 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(p)
      if (m1) {
        const [_, y, m, d] = m1
        return Date.UTC(Number(y), Number(m) - 1, Number(d))
      }
      const m2 = /^(\d{4})-(\d{2})$/.exec(p)
      if (m2) {
        const [_, y, m] = m2
        return Date.UTC(Number(y), Number(m) - 1, 1)
      }
      const t = Date.parse(p)
      if (!Number.isNaN(t)) return t
      return 0
    }

    const sortedDataPoints = [...dataPoints].sort((a, b) => {
      const ta = toTimestamp(
        typeof a.dimensions.timePeriod === 'string' ? a.dimensions.timePeriod : undefined
      )
      const tb = toTimestamp(
        typeof b.dimensions.timePeriod === 'string' ? b.dimensions.timePeriod : undefined
      )
      if (ta !== tb) return ta - tb
      const sa = typeof a.dimensions.timePeriod === 'string' ? a.dimensions.timePeriod : ''
      const sb = typeof b.dimensions.timePeriod === 'string' ? b.dimensions.timePeriod : ''
      return sa.localeCompare(sb)
    })

    // Build overhead-by-period map from indirectCosts when available
    const overheadByPeriod: Record<string, number> = {}
    const ic = salesTimeQuery.data?.indirectCosts
    if (ic?.groups && ic.groups.length > 0) {
      for (const g of ic.groups) {
        for (const [bucket, amount] of Object.entries(g.byTime || {})) {
          overheadByPeriod[bucket] = (overheadByPeriod[bucket] || 0) + (amount || 0)
        }
      }
      if (ic.othersByTime) {
        for (const [bucket, amount] of Object.entries(ic.othersByTime)) {
          overheadByPeriod[bucket] = (overheadByPeriod[bucket] || 0) + (amount || 0)
        }
      }
    }

    return sortedDataPoints.map((point): ProfitTimePoint => {
      const revenue = point.metrics.sales || 0
      const adSpend = point.metrics.advertising || 0
      const cogs = point.metrics.cogs || 0
      const direct = point.metrics.directCosts || 0
      const totalExpenses = adSpend + cogs + direct
      const cm2 =
        typeof point.metrics.cm2 === 'number' ? point.metrics.cm2 : revenue - totalExpenses

      const periodKey =
        typeof point.dimensions.timePeriod === 'string' ? point.dimensions.timePeriod : ''
      const allocatedForPeriod =
        overheadByPeriod[periodKey] ?? point.metrics.allocatedIndirectCosts ?? 0
      const profit = profitBasis === 'OC' ? cm2 - allocatedForPeriod : cm2

      return {
        period: periodKey,
        profit,
        revenue,
        cost: totalExpenses,
      }
    })
  }, [salesTimeQuery.data, profitBasis])

  return {
    data,
    isLoading: salesTimeQuery.isLoading,
    isError: salesTimeQuery.isError,
    error: salesTimeQuery.error?.message || null,
  }
}
